import { useState } from 'react';
import { Collapse, Divider, IconButton, Paper, Stack, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { formatMoney } from './money';

export interface CartSummaryLine {
  label: string;
  amount: number;
  note?: string;
  muted?: boolean;
}

export interface CartSummaryModel {
  lines: CartSummaryLine[];
  total: number;
  /** Kalan ödenecek tutar (cari/kısmi ödeme sonrası). */
  remaining?: number;
  currency?: string;
}

export interface CartSummaryProps {
  model: CartSummaryModel | null;
  title?: string;
  /** Masaüstünde yapışkan yan ray. */
  sticky?: boolean;
  /** Mobilde toplamı gösteren daraltılmış çubuk. */
  collapsible?: boolean;
}

/** Adımlar boyunca kalıcı fiyat özeti — `ShoppingCart` verisinden beslenir (Allocate'ten
 *  itibaren). `model` null ise hiçbir şey render edilmez. */
export function CartSummary({ model, title = 'Fiyat Özeti', sticky = false, collapsible = false }: CartSummaryProps) {
  const [open, setOpen] = useState(false);
  if (!model) return null;
  const { lines, total, remaining, currency } = model;

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        ...(sticky ? { position: { md: 'sticky' }, top: { md: 88 } } : {}),
      }}
    >
      <Stack
        direction="row"
        sx={{ alignItems: 'center', justifyContent: 'space-between', cursor: collapsible ? 'pointer' : 'default' }}
        onClick={collapsible ? () => setOpen((v) => !v) : undefined}
      >
        <Typography variant="subtitle2">{title}</Typography>
        {collapsible ? (
          <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
            <Typography variant="subtitle2">{formatMoney(remaining ?? total, currency)}</Typography>
            <IconButton size="small" sx={{ transform: open ? 'rotate(180deg)' : 'none' }}>
              <ExpandMoreIcon fontSize="small" />
            </IconButton>
          </Stack>
        ) : null}
      </Stack>

      <Collapse in={!collapsible || open} timeout="auto">
        <Stack spacing={0.75} sx={{ mt: 1.5 }}>
          {lines.map((line, i) => (
            <Stack key={i} direction="row" sx={{ justifyContent: 'space-between', gap: 2 }}>
              <Typography variant="body2" color={line.muted ? 'text.secondary' : 'text.primary'}>
                {line.label}
                {line.note && (
                  <Typography component="span" variant="caption" color="text.secondary">
                    {' '}
                    · {line.note}
                  </Typography>
                )}
              </Typography>
              <Typography variant="body2" color={line.muted ? 'text.secondary' : 'text.primary'} sx={{ whiteSpace: 'nowrap' }}>
                {formatMoney(line.amount, currency)}
              </Typography>
            </Stack>
          ))}
          <Divider sx={{ my: 0.5 }} />
          <Stack direction="row" sx={{ justifyContent: 'space-between' }}>
            <Typography variant="subtitle2">Toplam</Typography>
            <Typography variant="subtitle2">{formatMoney(total, currency)}</Typography>
          </Stack>
          {remaining != null && remaining !== total && (
            <Stack direction="row" sx={{ justifyContent: 'space-between' }}>
              <Typography variant="body2" color="text.secondary">
                Ödenecek
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                {formatMoney(remaining, currency)}
              </Typography>
            </Stack>
          )}
        </Stack>
      </Collapse>
    </Paper>
  );
}
